import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { Typography, Box, IconButton, Stack } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { theme, BackArrowStyle } from "services/constants";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { FilterSection } from "pages/FilterContext/FilterRow";
import {
  ApprovalDateRangeFilter,
  filterBarPickerSx,
} from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { ArrowBackIos } from "@mui/icons-material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  checkViewerPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

// The processing pipeline's fixed set of outcomes for a punch entry. The API
// filters server-side on the `status` query param using these exact keys.
const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Success", value: "success" },
  { label: "No User Onboarded", value: "no_user_onboarded" },
  { label: "No Tenant Mapping", value: "no_tenant_mapping" },
  { label: "Duplicate (Ignored)", value: "threshold_ignored" },
  {
    label: "Retry: Device Not Configured",
    value: "retry_device_not_configured",
  },
  { label: "Retry: Exception", value: "retry_exception" },
  { label: "Unrecorded Reason", value: "unrecorded" },
  { label: "Not Processed", value: "not_processed" },
];

// The employee identity columns lead every row; the rest come from whatever
// fields the punch entry itself carries.
const IDENTITY_COLUMNS = [
  { key: "employee_id", name: "Employee Id", type: "string" },
  { key: "employee_name", name: "Employee Name", type: "string" },
  { key: "department_name", name: "Department", type: "string" },
];
const IDENTITY_KEYS = new Set(IDENTITY_COLUMNS.map((column) => column.key));

// "log_date" / "log_time" (the split-out fields) aren't needed alongside
// "logdate" (the combined timestamp), and "downloaddate" isn't needed either.
const EXCLUDED_PUNCH_KEYS = new Set(["log_date", "log_time", "downloaddate"]);

// Known device-log field names get a friendlier label; anything else falls
// back to a generic humanized version of its key so a field the API adds
// later still shows up instead of being silently dropped.
const FIELD_NAME_OVERRIDES = {
  deviceid: "Device Id",
  downloaddate: "Download Date",
  date: "Date",
  userid: "Device User Id",
};

// CustomFilter's "refine" panel picks its comparison logic off each column's
// `type` — without one it silently matches every row. "date" gets its own
// date-range UI; "processed" is a real boolean; everything else is text.
const COLUMN_TYPE_OVERRIDES = {
  date: "date",
  processed: "boolean",
};

// These are the punch fields the API is known to return. They're always
// shown as columns — even when the date range comes back with zero rows to
// derive them from — so the header doesn't collapse down to just identity
// columns on an empty result.
const KNOWN_PUNCH_KEYS = [
  "date",
  "devicelogid",
  "deviceid",
  "userid",
  "logdate",
  "direction",
  "processed",
  "reason",
];

// Boolean fields (e.g. "processed") read as Yes/No rather than true/false.
// "reason" is only populated when processing failed, so an empty value means
// the punch went through fine — that reads better than a blank cell. "date"
// comes straight from the API as the log's "YYYY-MM-DD" key, so it's
// reformatted to match the rest of the app's DD-MM-YYYY convention.
const formatCellValue = (value, columnKey) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (columnKey === "reason") return value || "-";
  if (columnKey === "date" && value) {
    const parsedDate = moment(value, "YYYY-MM-DD", true);
    return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : value;
  }
  return value ?? "-";
};

// Columns aren't a fixed set — they're the identity columns followed by
// whichever punch fields are actually present across every row (a union,
// since not every punch is guaranteed to carry every key).
const buildColumns = (rows) => {
  const dynamicKeys = new Set(KNOWN_PUNCH_KEYS);
  rows.forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row)
        .filter(
          (key) =>
            !key.startsWith("_") &&
            !IDENTITY_KEYS.has(key) &&
            !EXCLUDED_PUNCH_KEYS.has(key),
        )
        .forEach((key) => dynamicKeys.add(key));
    }
  });

  // "reason" reads better as the last column since it's a free-text
  // explanation rather than a data point like the rest.
  const orderedKeys = Array.from(dynamicKeys).sort((first, second) => {
    if (first === "reason") return 1;
    if (second === "reason") return -1;
    return 0;
  });

  return [
    ...IDENTITY_COLUMNS,
    ...orderedKeys.map((key) => ({
      key,
      name:
        FIELD_NAME_OVERRIDES[key] ||
        key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      type: COLUMN_TYPE_OVERRIDES[key] || "string",
      // The download (Excel/PDF/CSV) reads straight from the raw row data,
      // bypassing the on-screen formatCellValue — so "date" needs its own
      // formatter to also export as DD-MM-YYYY instead of the raw
      // "YYYY-MM-DD" the API returns.
      ...(key === "date"
        ? { customFormatter: (row) => formatCellValue(row?.date, "date") }
        : {}),
    })),
  ];
};

// The API returns an object keyed by user id, each holding the employee's
// identity under `user_details` and their punches grouped by date under
// `logs` — e.g. { "3": { user_details: {...}, logs: { "2026-07-01": [{deviceid, downloaddate}] } } }.
// That's flattened here into one row per individual punch entry, tagged with
// the employee identity and the date it was logged under.
const flattenPunchRows = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }

  return Object.values(payload)
    .filter((entry) => entry && typeof entry === "object")
    .flatMap((entry) => {
      const userDetails = entry?.user_details || {};
      const logsByDate = entry?.logs || {};

      return Object.entries(logsByDate).flatMap(([date, entries]) => {
        const punchList = Array.isArray(entries)
          ? entries
          : [entries].filter(Boolean);

        return punchList.map((punch, index) => ({
          employee_name: userDetails?.first_name || "-",
          employee_id: userDetails?.employee_id ?? "-",
          department_name: userDetails?.department_name || "-",
          date,
          ...punch,
          // "devicelogid" is the one field guaranteed unique per punch
          // record. Unmapped-user punches (user_details is null, e.g.
          // NO_USER_ONBOARDED) all have no user_id to key off, so the old
          // `${user_id}-${date}-${index}` key collided across every such
          // punch sharing a date — which made React misrender/reuse rows
          // across re-renders (search, filter, pagination) instead of
          // showing each one distinctly.
          // The backend can also return the same devicelogid twice for one
          // user/date (duplicate punch records), so devicelogid alone is
          // not unique either. Duplicate keys left stale rows on screen
          // after search/filter, so combine user, date, id and position.
          _rowKey: `${punch?.userid ?? userDetails?.user_id ?? "u"}-${date}-${
            punch?.devicelogid ?? "x"
          }-${index}`,
        }));
      });
    });
};

// Embedded (inside the Punch Details "Report Type" switcher in WorkingHours)
// reuses that screen's own header, back button, and date range filter — this
// component only owns the table and its own data fetch. Standalone (from the
// Reports list), it renders full-page with everything including its own
// date range and status filter.
export const RawPunchEntryDetailsReport = ({
  setShowReport,
  embedded = false,
  startDate: controlledStartDate,
  endDate: controlledEndDate,
  departmentId,
}) => {
  const { domain, token, userDetails, userScreenAccess, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [punchRows, setPunchRows] = useState([]);
  const [filteredPunchRows, setFilteredPunchRows] = useState([]);
  const [statusLabel, setStatusLabel] = useState("All");
  const [statusValue, setStatusValue] = useState("all");
  // Standalone default is This Month through today (embedded mode is driven
  // by the parent's controlled dates instead).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [ownStartDate, setOwnStartDate] = useState(initialDateRange.startDate);
  const [ownEndDate, setOwnEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const startDate = embedded ? controlledStartDate : ownStartDate;
  const endDate = embedded ? controlledEndDate : ownEndDate;
  // Sticky toolbar/footer height reservation — same pattern as every other
  // modernized Reports screen (Anomalies, DeviceStatusReport, WorkingHours
  // Report): the table header's sticky `top` offset and the table
  // container's bottom padding both need to know these heights up front.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // subModuleNameMatches applies the same alias table checkViewerPermission
  // below relies on, instead of a raw === that would silently land on
  // undefined and send an invalid submodule id when saving a view — same
  // idiom every other TeamZone Reports screen uses.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Raw Punch Entry"),
  )?.sub_module_id;

  const checkAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Raw Punch Entry",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const updateStatusFilter = (event, data) => {
    setPage(0);
    if (!data) {
      setStatusLabel("All");
      setStatusValue("all");
      return;
    }
    setStatusLabel(data.label);
    setStatusValue(data.value || "all");
  };

  const handleStartDate = (selectedStartDate, selectedEndDate, rangeLabel) => {
    setOwnStartDate(moment(selectedStartDate).format("YYYY-MM-DD"));
    setOwnEndDate(moment(selectedEndDate).format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  useEffect(() => {
    const fetchRawPunchLogs = async () => {
      if (!startDate || !endDate) return;
      try {
        setIsLoading(true);
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          status: statusValue || "all",
          company_id: topbarCompanyId,
        });
        if (departmentId !== undefined && departmentId !== null) {
          params.set("department_id", departmentId);
        }
        const response = await fetch(
          `${domain}biometric-raw-logs/?${params.toString()}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setPunchRows(flattenPunchRows(res));
        } else if (response.status === 409) {
          setPunchRows([]);
          sessionHandling();
        } else {
          setPunchRows([]);
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[RawPunchEntryDetails] operation failed:", error);
        setPunchRows([]);
        showToast(
          "error",
          "Couldn't load the raw punch entry details. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchRawPunchLogs();
  }, [
    domain,
    token,
    startDate,
    endDate,
    statusValue,
    departmentId,
    topbarCompanyId,
  ]);

  const columns = useMemo(() => buildColumns(punchRows), [punchRows]);

  // The Default View column picker every other Reports screen already has —
  // this one was the one left behind. Columns here are dynamic (derived
  // from whatever fields the current date range's punches actually carry,
  // see buildColumns above) rather than a fixed header list, but the hook
  // handles that fine: a saved view referencing a key that's since dropped
  // out of `columns` is just filtered out of that view's headerConfig.
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "raw_punch_entry_saved_views",
    fullHeaders: columns,
    screenAccess: checkAccess,
    onViewChange: handleSavedViewChange,
  });

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredPunchRows(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    return {
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      Status: statusLabel || "All",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    };
  }, [endDate, startDate, statusLabel]);

  return isLoading ? (
    <Loader />
  ) : (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        {/* This report only ever renders embedded inside WorkingHours'
            report-type switcher, which itself renders inside TeamZone
            Reports' grouped category page — both already supply
            designShell.pageSurface (background + min-height) and page
            padding. BoxStyle's own p:2 + light background used to double-
            stack a visibly different card on top of the parent's, which is
            why this screen looked different from every sibling report. */}
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {!embedded && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <IconButton
                onClick={() => setShowReport(false)}
                sx={BackArrowStyle}
              >
                <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
              </IconButton>

              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
                >
                  Raw Punch Entry Details
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Raw biometric punch log entries as recorded by the device.
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Embedded inside Punch Details, the parent screen already shows
              its own date range + Report Type switcher, and this report
              doesn't need a status filter — so nothing to show here. */}
          {!embedded && (
            <FilterSection
              filterLabel="Filters"
              filters={[
                {
                  width: 350,
                  component: (
                    <ApprovalDateRangeFilter
                      thisMonthToDate
                      startDate={startDate}
                      endDate={endDate}
                      dateRangeLabel={dateRangeLabel}
                      onChange={handleStartDate}
                      showToday={true}
                      includeCurrentDate={true}
                      allowFutureDate={false}
                      sx={filterBarPickerSx}
                    />
                  ),
                },
                {
                  width: 300,
                  props: {
                    dataList: STATUS_OPTIONS,
                    selectedValue: statusLabel,
                    updateDataFunction: updateStatusFilter,
                    Icons: TaskAltIcon,
                    staticLabel: "Status",
                  },
                },
              ]}
            />
          )}

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Raw Punch Entry Details"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={punchRows}
              filteredData={filteredPunchRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={checkAccess}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.Box>
              <Mui.TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                  borderLeft: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  borderBottom: `1px solid ${designColors.slate200}`,
                }}
              >
                <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((column, columnIndex) => (
                        <Mui.TableCell
                          key={column.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            ...(columnIndex === 0
                              ? { borderTopLeftRadius: 0 }
                              : {}),
                            ...(columnIndex === visibleHeaders.length - 1
                              ? { borderTopRightRadius: 0 }
                              : {}),
                          }}
                        >
                          {column.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {filteredPunchRows?.length === 0 ? (
                      <TableEmptyState dataLength={punchRows} />
                    ) : (
                      filteredPunchRows
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        ?.map((row, index) => (
                          <Mui.TableRow
                            key={row?._rowKey ?? index}
                            hover
                            sx={{
                              ...designShell.tableRowHover,
                              "&:last-child td": { borderBottom: "none" },
                              "& td": { backgroundColor: designColors.white },
                            }}
                          >
                            {visibleHeaders.map((column) => {
                              const cellValue = formatCellValue(
                                row?.[column.key],
                                column.key,
                              );

                              if (column.key === "reason") {
                                return (
                                  <Mui.TableCell
                                    key={column.key}
                                    sx={{
                                      ...approveTableBodyCell,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: 220,
                                    }}
                                  >
                                    <Mui.Tooltip arrow title={cellValue}>
                                      <span>{cellValue}</span>
                                    </Mui.Tooltip>
                                  </Mui.TableCell>
                                );
                              }

                              return (
                                <Mui.TableCell
                                  key={column.key}
                                  sx={{
                                    ...approveTableBodyCell,
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    maxWidth: 220,
                                  }}
                                >
                                  {cellValue}
                                </Mui.TableCell>
                              );
                            })}
                          </Mui.TableRow>
                        ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            </Mui.Box>

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: filteredPunchRows?.length || 0,
                itemLabel: "punches",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
